import api from '../api/api'

/**
 * Backend stores latitude/longitude as Decimal(max_digits=9, decimal_places=6),
 * i.e. up to 3 digits before the point and 6 after. Browser geolocation often
 * returns 10+ decimals (e.g. -6.7760123456) which fails server-side validation
 * with: "Ensure that there are no more than 9 digits in total".
 *
 * This helper rounds to 6 decimal places (≈11 cm precision — more than enough
 * for delivery) and additionally guarantees the total digit count is ≤ 9.
 */
const sanitizeCoord = value => {
  if (value === null || value === undefined || value === '') return value
  const num = typeof value === 'number' ? value : parseFloat(value)
  if (Number.isNaN(num)) return value
  // Round to 6 decimal places.
  let rounded = Math.round(num * 1e6) / 1e6
  // Guard: if integer part is unusually large, trim decimals further so total
  // digits ≤ 9. (Practically never triggered for valid lat/lng, but safe.)
  const intDigits = Math.floor(Math.abs(rounded)).toString().length
  const decimalsAllowed = Math.max(0, 9 - intDigits)
  const factor = 10 ** decimalsAllowed
  rounded = Math.round(rounded * factor) / factor
  return rounded
}

const sanitizeAddressPayload = payload => {
  if (!payload || typeof payload !== 'object') return payload
  const next = { ...payload }
  if ('latitude' in next) next.latitude = sanitizeCoord(next.latitude)
  if ('longitude' in next) next.longitude = sanitizeCoord(next.longitude)
  return next
}

/**
 * List the authenticated user's saved delivery addresses.
 * Returns the raw paginated envelope (`{ count, next, previous, results }`).
 */
export const listAddresses = async () => {
  const res = await api.get('/orders/addresses/')
  return res.data
}

/**
 * Retrieve a single address by id.
 */
export const getAddress = async id => {
  const res = await api.get(`/orders/addresses/${id}/`)
  return res.data
}

/**
 * Create a new saved address.
 * Required: `label` and either (`latitude`+`longitude`) or `street_address`.
 */
export const createAddress = async payload => {
  const res = await api.post('/orders/addresses/', sanitizeAddressPayload(payload))
  return res.data
}

/**
 * Partial update (recommended over PUT).
 */
export const updateAddress = async (id, payload) => {
  const res = await api.patch(`/orders/addresses/${id}/`, sanitizeAddressPayload(payload))
  return res.data
}

/**
 * Flip an address to default without running full-object validation server-side.
 */
export const setDefaultAddress = async id => {
  const res = await api.post(`/orders/addresses/${id}/set-default/`)
  return res.data
}

/**
 * Delete a saved address.
 */
export const deleteAddress = async id => {
  const res = await api.delete(`/orders/addresses/${id}/`)
  return res.data
}

/**
 * Activate this address as the user's current `UserLocation` (powers home feed etc.).
 */
export const setAddressAsLocation = async addressId => {
  const res = await api.post('/auth/location/from-address', { address_id: addressId })
  return res.data
}

/**
 * Public geocode / validate helper. Body accepts either
 *   { address: 'free text' }
 * or
 *   { latitude, longitude }.
 */
export const validateAddress = async payload => {
  const res = await api.post('/orders/addresses/validate/', sanitizeAddressPayload(payload))
  return res.data
}
